import { useState } from "react"
import type { ComponentProps } from "react"
import Separator from "@/v2-ui/separator"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import { TooltipLink } from "@/v2-backoffice-client/components/link"
import { TooltipButton } from "@/v2-backoffice-client/components/button"
import ActionMenu from "@/v2-backoffice-client/components/actionMenu"
import CustomerMultiSessionMenu from "@/v2-backoffice-client/customer/multiSession/CustomerMultiSessionMenu"
import CustomerEditDialog from "@/v2-backoffice-client/customer/edit/CustomerEditDialog"
import CustomerCompanyLinkDialog from "@/v2-backoffice-client/customer/company/CustomerCompanyLinkDialog"
import { buildMixpanelBoardUrl } from "@/v2-backoffice-client/analytics/analytics.utils"
import {
  FENERUM_ACCOUNT_BASE_URL,
  HUBSPOT_BASE_URL,
} from "@/v2-backoffice-client/constants"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"

export type CustomerViewToolbarProps = ComponentProps<"div"> & {
  customer: CustomerFragment | null | undefined
}

function CustomerViewToolbar(props: CustomerViewToolbarProps) {
  const { customer, ...viewSheetToolbarProps } = props
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isLinkOpen, setIsLinkOpen] = useState(false)
  if (!customer) return null

  return (
    <ViewSheet.Toolbar {...viewSheetToolbarProps}>
      <TooltipButton
        variant="ghost"
        isStacked
        iconSrc="edit"
        content="Update the app-owned identity fields (pushed to HubSpot on save)"
        onClick={() => {
          setIsEditOpen(true)
        }}
      >
        Update
      </TooltipButton>

      <Separator orientation="vertical" />

      <TooltipLink
        variant="ghost"
        isStacked
        iconSrc="mixpanel"
        content="Open in Mixpanel"
        to={buildMixpanelBoardUrl({
          resourceType: "event",
          propertyName: "Customer.id",
          propertyType: "number",
          filterOperator: "equals",
          filterValue: String(customer.Id),
        })}
      >
        Mixpanel
      </TooltipLink>

      {/* One HubSpot entry: the deep link and the link management live in
          the same menu — the standalone "Linked" button read as its own
          feature. Unlinked customers get the "Link a company" action here
          too, so the button never disables. */}
      <ActionMenu>
        <ActionMenu.Trigger
          isStacked
          iconSrc="hubspot"
          content={
            customer.Company
              ? "Open in HubSpot, or view / unlink the linked company"
              : "Link a HubSpot company to this customer"
          }
        >
          HubSpot
        </ActionMenu.Trigger>

        <ActionMenu.Content>
          {customer.ExternalCustomerId && (
            <ActionMenu.LinkItem
              iconSrc="hubspot"
              to={`${HUBSPOT_BASE_URL}/${customer.ExternalCustomerId}`}
              external
            >
              Open in HubSpot
            </ActionMenu.LinkItem>
          )}

          <ActionMenu.Item
            iconSrc="link"
            onSelect={() => {
              setIsLinkOpen(true)
            }}
          >
            {customer.Company ? "View / unlink company" : "Link a company"}
          </ActionMenu.Item>
        </ActionMenu.Content>
      </ActionMenu>

      <TooltipLink
        variant="ghost"
        isStacked
        iconSrc="bank"
        content={
          customer.BillingAccountUuid
            ? "Open the billing account in Fenerum"
            : "No linked Fenerum billing account"
        }
        disabled={!customer.BillingAccountUuid}
        to={
          customer.BillingAccountUuid
            ? `${FENERUM_ACCOUNT_BASE_URL}/${customer.BillingAccountUuid}/`
            : undefined
        }
      >
        Fenerum
      </TooltipLink>

      <Separator orientation="vertical" />

      <CustomerMultiSessionMenu customerId={customer.Id} />

      <CustomerEditDialog
        customer={customer}
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
      />

      <CustomerCompanyLinkDialog
        customer={customer}
        open={isLinkOpen}
        onOpenChange={setIsLinkOpen}
      />
    </ViewSheet.Toolbar>
  )
}

export default CustomerViewToolbar
